import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "@/App.css";
import { Phone, Menu, X, ChevronRight, ChevronDown, Instagram, Facebook, Sparkles, Zap, Heart, Eye, Hand, Scissors, MessageSquare, Star, Quote, Clock, Shield, HelpCircle, Calendar } from "lucide-react";
import { Toaster, toast } from "sonner";
import { EpilationLaserPage, SoinsVisagePage, ExtensionsCilsPage } from "./pages/SEOPages";
import { trackContact, trackLead, trackViewContent, trackSchedule } from "./utils/metaPixel";

// Phone number
const PHONE_NUMBER = "5145545916";

// Images
const IMAGES = {
  logo: "https://customer-assets.emergentagent.com/job_beaute-signemelanie/artifacts/jv7kdtvf_IMG_4257.jpeg",
  owner: "https://customer-assets.emergentagent.com/job_beaute-signemelanie/artifacts/ychf09k5_IMG_4258.jpeg",
  laser: "https://customer-assets.emergentagent.com/job_aesthetique-mg/artifacts/5d0qtrmo_att.RWHmydXpAAei7Szu7oBdDmEkOc5vyXvABCo7CTr--9M.jpeg",
  esthedermLogo: "https://customer-assets.emergentagent.com/job_beaute-signemelanie/artifacts/uaqpc4hj_IMG_4389.png",
  storefront1: "https://customer-assets.emergentagent.com/job_68538375-794f-46e6-9b23-d3a9f68e86c7/artifacts/4kwcus56_att.zIsf7DA2iHK4PqquQbSrXZzBEmUMYQa3h86X2jqWDlw.jpeg",
  pedicure: "https://customer-assets.emergentagent.com/job_68538375-794f-46e6-9b23-d3a9f68e86c7/artifacts/5hhbmge8_att.O4cwIIwiyVRkZsHqDEuKkSND4DPn8137j4jICBfxhJE.jpeg",
  storefront2: "https://customer-assets.emergentagent.com/job_68538375-794f-46e6-9b23-d3a9f68e86c7/artifacts/zqsv7yba_att.O3NaNKiN5Ts7s5Eapbhw7otyK8JnZr8GQGIOH6axpyk.jpeg",
  interior: "https://customer-assets.emergentagent.com/job_68538375-794f-46e6-9b23-d3a9f68e86c7/artifacts/ro2xrdl3_att.dQKhlEAJN8_dCu_j9s5COBRZqBBCLnfokg45Gs-xmjk.jpeg",
};

// Social Links
const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/share/1CDts9LfKS/?mibextid=wwXIfr",
  instagram: "https://www.instagram.com/esthetiquesignemg?igsh=ZDVtdHZwaHRlNWlj",
  messenger: "https://m.me/esthetiquesignemelanie"
};

// Lien de réservation en ligne (Square Appointments)
const BOOKING_URL = "https://book.squareup.com/appointments/861ugx3et898sw/location/K53TET2676NYW/services";

// Sticky CTA Button - Always visible
const StickyBookingButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!visible) return null;

  const handleClick = () => {
    trackSchedule();
  };

  return (
    <a
      href={BOOKING_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-6 py-4 bg-[#5C4B3F] text-white font-medium text-sm uppercase tracking-wider shadow-2xl hover:bg-[#8B7355] transition-all duration-300 hover:scale-105"
      data-testid="sticky-cta"
    >
      <Calendar size={18} />
      <span>Réserver</span>
    </a>
  );
};

// Compact Navigation
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleReserveClick = () => {
    trackSchedule();
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-sm py-3" : "bg-transparent py-4"}`}>
      <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-2 relative">
          <div className={`h-[60px] w-[60px] flex items-center justify-center ${scrolled ? 'bg-white' : 'bg-[#F9F8F6]'} transition-colors duration-300`}>
            <img 
              src={IMAGES.logo}
              alt="Logo MG"
              className="h-[54px] w-[54px] object-contain"
              style={{ 
                mixBlendMode: 'multiply'
              }}
            />
          </div>
        </a>
        <a 
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleReserveClick}
          className="px-5 py-2 bg-[#5C4B3F] text-white text-xs uppercase tracking-wider hover:bg-[#8B7355] transition-colors"
        >
          Réserver
        </a>
      </div>
    </nav>
  );
};

// 1. HERO SECTION - Promise + CTA
const HeroSection = () => (
  <section id="hero" className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-[#F9F8F6] via-[#F5F2EE] to-[#EBE6E0]" data-testid="hero-section">
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-20 right-20 w-64 h-64 rounded-full border border-[#5C4B3F]/10"></div>
      <div className="absolute bottom-20 left-10 w-32 h-32 border border-[#8B7355]/10 rotate-45"></div>
    </div>

    <div className="relative z-10 max-w-6xl mx-auto px-4 py-20 grid lg:grid-cols-2 gap-12 items-center">
      {/* Left - Content */}
      <div className="text-center lg:text-left">
        <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-4">28 ans d'expertise</p>
        <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-[#5C4B3F] leading-tight mb-6">
          Parce que vous êtes unique,
          <span className="block italic font-light text-[#8B7355] mt-2">offrez-vous des soins d'exception</span>
          <span className="block text-[#5C4B3F] text-2xl sm:text-3xl mt-2">signés Mélanie Gauthier!</span>
        </h1>
        <p className="text-[#5C4B3F]/70 text-lg mb-8 max-w-md mx-auto lg:mx-0">
          Épilation laser permanente, soins du visage et beauté des mains.
        </p>
        
        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
          <a 
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#5C4B3F] text-white text-sm uppercase tracking-wider hover:bg-[#8B7355] transition-all hover:-translate-y-1"
            data-testid="hero-cta"
          >
            <Calendar size={18} />
            Prendre rendez-vous
          </a>
          <a 
            href="#services" 
            className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#5C4B3F] text-[#5C4B3F] text-sm uppercase tracking-wider hover:bg-[#5C4B3F] hover:text-white transition-all"
          >
            Voir les services
            <ChevronDown size={18} />
          </a>
        </div>

        {/* Quick stats */}
        <div className="flex gap-8 mt-10 justify-center lg:justify-start">
          <div className="text-center">
            <p className="font-['Playfair_Display'] text-3xl text-[#5C4B3F]">28+</p>
            <p className="text-[#5C4B3F]/60 text-xs uppercase">Ans d'expérience</p>
          </div>
          <div className="text-center">
            <p className="font-['Playfair_Display'] text-3xl text-[#5C4B3F]">2000+</p>
            <p className="text-[#5C4B3F]/60 text-xs uppercase">Clientes satisfaites</p>
          </div>
          <div className="text-center">
            <p className="font-['Playfair_Display'] text-3xl text-[#5C4B3F]">100%</p>
            <p className="text-[#5C4B3F]/60 text-xs uppercase">Sécuritaire</p>
          </div>
        </div>
      </div>

      {/* Right - Image */}
      <div className="relative">
        <div className="relative">
          <img 
            src={IMAGES.laser} 
            alt="Épilation laser permanente Soprano ICE Platinum - résultats sans douleur" 
            className="w-full h-[500px] object-cover shadow-2xl"
          />
          {/* Badge */}
          <div className="absolute -bottom-4 -left-4 bg-[#5C4B3F] text-white px-6 py-3">
            <p className="text-xs uppercase tracking-wider">Technologie</p>
            <p className="font-['Playfair_Display'] text-lg">Soprano ICE Platinum</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// 2. SERVICES SECTION - With prices (emphasis on Laser)
const ServicesSection = () => {
  const services = [
    { 
      icon: <Sparkles size={32} />, 
      title: "Soins du Visage", 
      desc: "Protocoles personnalisés avec les produits Institut Esthéderm pour révéler l'éclat de votre peau. Traitements anti-âge, hydratation, éclat et purifiants adaptés à vos besoins spécifiques."
    },
    { 
      icon: <Eye size={32} />, 
      title: "Extensions de Cils", 
      desc: "Sublimez votre regard avec des extensions de cils naturelles et personnalisées. Pose classique ou volume russe, effet naturel ou glamour selon vos envies."
    },
    { 
      icon: <Hand size={32} />, 
      title: "Manucure", 
      desc: "Soins complets des mains et des ongles pour des mains impeccables. Manucure régulière, pose de vernis, soins hydratants et réparateurs."
    },
    { 
      icon: <Heart size={32} />, 
      title: "Pédicure", 
      desc: "Soins relaxants et complets des pieds dans un environnement apaisant. Pédicure spa, traitement des callosités, pose de vernis et massage."
    },
    { 
      icon: <Scissors size={32} />, 
      title: "Épilation à la Cire", 
      desc: "Épilation traditionnelle avec des cires de qualité professionnelle. Visage, corps, bikini - une peau douce et lisse pendant plusieurs semaines."
    },
    { 
      icon: <Sparkles size={32} />, 
      title: "Pose d'ongles", 
      desc: "Pose d'ongles en résine et poudre : une technique durable qui offre des ongles solides, élégants et impeccables pour une tenue longue durée au quotidien."
    }
  ];

  return (
    <section id="services" className="py-20 bg-white" data-testid="services-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-2">Nos Services</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#5C4B3F] mb-4">
            Services Offerts
          </h2>
          <div className="w-24 h-1 bg-[#8B7355]/30 mx-auto"></div>
        </div>

        {/* Featured Service - Laser */}
        <div className="mb-12">
          <div className="bg-gradient-to-r from-[#5C4B3F] to-[#8B7355] p-8 md:p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 text-xs uppercase tracking-wider mb-4">
                  <Star size={12} /> Service vedette
                </div>
                <h3 className="font-['Playfair_Display'] text-3xl mb-4">Épilation Laser Soprano ICE Platinum</h3>
                <p className="text-white/80 mb-6">
                  La technologie la plus avancée pour une épilation permanente. Rapide, efficace et pratiquement indolore. Convient à tous les types de peau.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <ChevronRight size={16} /> Tous types de peau et de poils
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <ChevronRight size={16} /> Technologie 3-en-1
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <ChevronRight size={16} /> Système de refroidissement intégré
                  </li>
                </ul>
                <a 
                  href="#tarifs" 
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#5C4B3F] text-sm uppercase tracking-wider hover:bg-[#F9F8F6] transition-colors"
                >
                  Voir les tarifs laser
                </a>
              </div>
              <div className="hidden md:block">
                <img src={IMAGES.laser} alt="Traitement épilation laser Soprano ICE Platinum à Saint-Hilaire" className="w-full h-64 object-cover" />
              </div>
            </div>
          </div>
        </div>

        {/* Other Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="group p-8 bg-[#F9F8F6] hover:bg-[#5C4B3F] transition-all duration-300"
              data-testid={`service-${index}`}
            >
              <div className="text-[#5C4B3F] group-hover:text-white mb-4 transition-colors">
                {service.icon}
              </div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#5C4B3F] group-hover:text-white mb-3 transition-colors">
                {service.title}
              </h3>
              <p className="text-[#5C4B3F]/70 group-hover:text-white/80 text-sm leading-relaxed transition-colors">
                {service.desc}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a 
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#5C4B3F] text-white text-sm uppercase tracking-wider hover:bg-[#8B7355] transition-colors"
          >
            <Calendar size={18} />
            Réserver maintenant
          </a>
        </div>
      </div>
    </section>
  );
};

// Laser Pricing Section
const LaserPricingSection = () => {
  // Track ViewContent when this section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackViewContent('Tarifs Laser', 'Pricing');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    const section = document.getElementById('tarifs');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const pricingLeft = [
    { zone: "Lèvre supérieure", price: "70$" },
    { zone: "Menton", price: "50$" },
    { zone: "Visage complet", price: "120$" },
    { zone: "Aisselles", price: "70$" },
    { zone: "Bikini régulier", price: "80$" },
    { zone: "Bikini échancré", price: "100$ à 150$" },
    { zone: "Bikini complet", price: "160$" },
  ];

  const pricingRight = [
    { zone: "Demi-jambes", price: "160$" },
    { zone: "Jambes complètes", price: "300$" },
    { zone: "Cuisses", price: "185$" },
    { zone: "Demi-bras", price: "95$" },
    { zone: "Bras complet", price: "135$" },
    { zone: "Dos ou torse", price: "195$" },
    { zone: "Épaules ou nuque", price: "75$" },
  ];

  return (
    <section id="tarifs" className="py-20 bg-[#F9F8F6]" data-testid="pricing-section">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-2">Tarifs Laser</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#5C4B3F] mb-4">
            Prix par séance
          </h2>
          <div className="flex justify-center gap-4 flex-wrap">
            <span className="px-4 py-2 bg-[#5C4B3F]/10 text-[#5C4B3F] text-sm">
              2 régions = <strong>10% rabais</strong>
            </span>
            <span className="px-4 py-2 bg-[#5C4B3F] text-white text-sm">
              3 régions = <strong>20% rabais</strong>
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-0">
            {pricingLeft.map((item, index) => (
              <div key={index} className="flex justify-between py-3 border-b border-[#5C4B3F]/10">
                <span className="text-[#5C4B3F]">{item.zone}</span>
                <span className="text-[#5C4B3F] font-medium">{item.price}</span>
              </div>
            ))}
          </div>
          <div className="space-y-0">
            {pricingRight.map((item, index) => (
              <div key={index} className="flex justify-between py-3 border-b border-[#5C4B3F]/10">
                <span className="text-[#5C4B3F]">{item.zone}</span>
                <span className="text-[#5C4B3F] font-medium">{item.price}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-10">
          <a 
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#5C4B3F] text-white text-sm uppercase tracking-wider hover:bg-[#8B7355] transition-colors"
          >
            Réserver une consultation gratuite
          </a>
        </div>
      </div>
    </section>
  );
};

// 3. SOCIAL PROOF - Testimonials + Salon Photos
const GOOGLE_REVIEWS_URL = "https://www.google.com/maps/place/Esth%C3%A9tique+Sign%C3%A9+M%C3%A9lanie+Gauthier+-+%C3%89pilation+laser+Mont+Saint-Hilaire/data=!4m8!3m7!1s0x4cc9ab8aa7256d13:0xe403ec562dc79b5b!9m1!1b1?hl=fr";

const SocialProofSection = () => {
  const [showAllReviews, setShowAllReviews] = useState(false);

  const testimonials = [
    {
      name: "annie labelle",
      text: "Je recommande vivement Mélanie pour vos soins de laser ! C'est une technicienne très professionnelle, attentionnée et d'une grande gentillesse. Dès la première séance, elle a su me mettre totalement en confiance et je me suis sentie en parfaite sécurité entre ses mains. Merci encore pour ton approche si rassurante et ton accueil.",
      source: "Google",
      date: "Il y a 1 mois",
      rating: 5
    },
    {
      name: "Claudine G.",
      text: "Merci Mélanie pour mes beaux cils et les soins pour ma peau. Cela fait plus de 15 ans que Mélanie me conseille pour maintenir une apparence jeune malgré une peau très sensible. Elle a toujours été professionnelle et à l'écoute de mes besoins.",
      source: "Google",
      date: "Il y a 1 mois",
      rating: 5
    },
    {
      name: "Noémie Cordeau",
      text: "J'ai eu une excellente expérience dans cette clinique esthétique. C'est vraiment l'endroit idéal pour celles qui recherchent un service plus personnalisé, dans une ambiance intime, chaleureuse et rassurante. Mélanie est extrêmement professionnelle et hautement qualifiée. Elle met rapidement à l'aise dès les premières minutes et porte une attention sincère au confort de ses clientes, ce qui fait toute la différence.",
      source: "Google",
      date: "Il y a 2 mois",
      rating: 5
    },
    {
      name: "Eve Gagnon",
      text: "Cela fait 10 ans que je vais voir Mélanie, depuis mon adolescence jusqu'à mes 24 ans, elle a su m'accompagner dans tous mes moments de vie. Elle est à l'écoute, empathique et flexible. Elle a ma confiance à 100% et je suis ultra satisfaite de ses services et conseils.",
      source: "Google",
      date: "Il y a 2 mois",
      rating: 5
    },
    {
      name: "Maude Massé",
      text: "Je vais chez Mélanie depuis maintenant 6 ans et je ne pourrais pas être plus satisfaite ! Son professionnalisme, sa douceur et son souci du détail font toute la différence. On se sent toujours bien accueillis et à l'aise ! Je la recommande sans hésitation à tous ceux qui recherchent quelqu'un de confiance avec beaucoup de talent !",
      source: "Google",
      date: "Il y a 2 mois",
      rating: 5
    },
    {
      name: "Sophie Jerome",
      text: "Une expérience unique à chaque visite. Mélanie est attentionnée et professionnelle. Les soins et la gamme de produits offerts sont de grande qualité. Je recommande pleinement !",
      source: "Google",
      date: "Il y a 2 mois",
      rating: 5
    },
    {
      name: "Melanie Lewis",
      text: "Une esthéticienne en or !!! Elle est tout simplement parfaite ! Toujours à l'écoute de nos besoins, toujours d'un professionnalisme incroyable et d'une douceur magnifique ! Merci pour tout mon esthéticienne, mon amie ❤️",
      source: "Google",
      date: "Il y a 2 mois",
      rating: 5
    },
    {
      name: "Nancy Majdalani",
      text: "J'adore aller chez Mélanie ! Elle est toujours accueillante, professionnelle et met tout de suite à l'aise. Elle connaît parfaitement ses soins et prend le temps d'expliquer chaque étape, ce qui fait toute la différence. On sent qu'elle est passionnée par ce qu'elle fait. En plus, elle est très disponible et flexible pour les rendez-vous, ce qui est vraiment apprécié. Je recommande sans hésitation !",
      source: "Google",
      date: "Il y a 3 mois",
      rating: 5
    },
    {
      name: "Carina Lewis",
      text: "Mélanie est professionnelle, minutieuse et très compétente ! Elle est toujours à l'écoute de ses clientes et offre un service impeccable. Je la recommande fortement !!",
      source: "Google",
      date: "Il y a 3 mois",
      rating: 5
    },
    {
      name: "Audrey Collin",
      text: "Cela fait plus de 12 ans que je vais voir Mélanie pour mes soins. Passionnée par son domaine, elle a vraiment une approche humaine et en douceur. Elle est toujours à l'écoute et elle donne un vrai service personnalisé à ses clientes. C'est une vraie perle ❤️",
      source: "Google",
      date: "Il y a 6 mois",
      rating: 5
    }
  ];

  const visibleTestimonials = showAllReviews ? testimonials : testimonials.slice(0, 3);

  // Logo Google (SVG multicolore)
  const GoogleIcon = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );

  return (
    <section id="avis" className="py-20 bg-white" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-2">Témoignages</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#5C4B3F]">
            Ce que nos clientes disent
          </h2>
          <div className="flex items-center justify-center gap-2 mt-4">
            <GoogleIcon size={20} />
            <span className="text-[#5C4B3F]/70 text-sm">5,0 étoiles sur Google (31 avis)</span>
          </div>
        </div>

        {/* Testimonials */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {visibleTestimonials.map((testimonial, index) => (
            <a
              key={index}
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`google-review-card-${index}`}
              className="bg-[#F9F8F6] p-6 relative block transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              <Quote className="absolute top-4 right-4 text-[#5C4B3F]/10" size={40} />
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} className="fill-[#8B7355] text-[#8B7355]" />
                ))}
              </div>
              <p className="text-[#5C4B3F]/80 mb-4 italic">"{testimonial.text}"</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#5C4B3F] font-medium">{testimonial.name}</p>
                  <p className="text-[#8B7355] text-sm">{testimonial.source} • {testimonial.date}</p>
                </div>
                <GoogleIcon size={16} />
              </div>
            </a>
          ))}
        </div>

        {/* Toggle voir plus/moins */}
        {testimonials.length > 3 && (
          <div className="flex justify-center mb-8">
            <button
              onClick={() => setShowAllReviews(!showAllReviews)}
              data-testid="toggle-more-reviews-btn"
              className="inline-flex items-center gap-2 text-[#5C4B3F] hover:text-[#8B7355] transition-colors text-sm font-medium tracking-wide uppercase"
            >
              {showAllReviews ? "Voir moins" : `Voir ${testimonials.length - 3} avis de plus`}
              <ChevronDown
                size={16}
                className={`transition-transform duration-300 ${showAllReviews ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        )}

        {/* CTA vers tous les avis Google */}
        <div className="flex justify-center mb-16">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="see-all-google-reviews-btn"
            className="inline-flex items-center gap-3 bg-white border border-[#5C4B3F]/20 px-6 py-3 rounded-full shadow-sm hover:shadow-md hover:border-[#8B7355] transition-all duration-300 group"
          >
            <GoogleIcon size={22} />
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="fill-[#F9AB00] text-[#F9AB00]" />
                ))}
              </div>
              <span className="text-[#5C4B3F] font-medium text-sm">5,0</span>
            </div>
            <span className="text-[#5C4B3F] text-sm md:text-base">
              Voir les 31 avis Google
            </span>
            <ChevronRight size={18} className="text-[#8B7355] group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* Salon Photos */}
        <div className="text-center mb-8">
          <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-2">Notre Salon</p>
          <h3 className="font-['Playfair_Display'] text-2xl text-[#5C4B3F]">
            Un espace pensé pour vous
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <img src={IMAGES.storefront1} alt="Façade du salon Esthétique Signé Mélanie Gauthier à Saint-Hilaire" className="w-full h-48 object-cover" />
          <img src={IMAGES.interior} alt="Intérieur du salon d'esthétique - cabine de soins" className="w-full h-48 object-cover" />
          <img src={IMAGES.pedicure} alt="Espace pédicure et soins des pieds" className="w-full h-48 object-cover" />
          <img src={IMAGES.storefront2} alt="Entrée du salon d'esthétique Mélanie Gauthier" className="w-full h-48 object-cover" />
        </div>
      </div>
    </section>
  );
};

// 4. ABOUT - Human Side
const AboutSection = () => (
  <section id="apropos" className="py-20 bg-[#F9F8F6]" data-testid="about-section">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        {/* Image */}
        <div className="relative lg:sticky lg:top-24">
          <img 
            src={IMAGES.owner} 
            alt="Mélanie Gauthier, esthéticienne-propriétaire avec 28 ans d'expérience à Saint-Hilaire" 
            className="w-full h-[500px] object-cover object-top"
          />
          <div className="absolute bottom-4 left-4 right-4 bg-white p-4">
            <p className="font-['Playfair_Display'] text-xl text-[#5C4B3F]">Mélanie Gauthier</p>
            <p className="text-[#8B7355] text-sm">Esthéticienne-propriétaire</p>
            <p className="text-[#5C4B3F]/60 text-xs">Institut Esthéderm • Esthétique Signé Mélanie Gauthier</p>
          </div>
        </div>

        {/* Content */}
        <div>
          <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-2">À Propos</p>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#5C4B3F] mb-6">
            À mes clientes et futures clientes,
          </h2>
          <div className="space-y-4 text-[#5C4B3F]/80 text-sm leading-relaxed">
            <p>
              J'ai toujours su que je deviendrais esthéticienne. Toute petite, j'installais une salle d'attente avec mes poupées, je les maquillais, les dorlotais. Sans le savoir, je jouais à l'esthéticienne.
            </p>
            <p>
              J'ai découvert l'amour du métier dès mes premières expériences de travail. C'est ainsi que <strong>28 ans d'expérience plus tard</strong>, ma clinique se spécialise en <strong>épilation laser</strong>, soins de la peau, soins des pieds et des mains, extension de cils. Vous faire passer un moment de détente ou bien vous offrir la meilleure technologie d'épilation laser dans une ambiance chic et conviviale est pour moi une réelle priorité.
            </p>
            <p>
              Parce que je veux que chaque rendez-vous soit une expérience unique au-delà d'un bien-être physique, c'est avec beaucoup d'attention que j'écoute vos besoins et réponds à vos attentes.
            </p>
            <p>
              Plus qu'une passion, mon travail d'esthéticienne demande de la rigueur et du professionnalisme, ce pourquoi, je continue de me perfectionner et de suivre les dernières tendances afin de vous conseiller adéquatement.
            </p>
            <p>
              <strong>Mon engagement:</strong> vous faire sentir en toute confiance et sécurité pour vos traitements! Donner un coup d'éclat à votre beauté naturelle à l'aide d'une gamme complète de soins (sans technologie, sans aiguille, un vrai bon soin à la main) et produits paramédicaux <strong>Institut Esthéderm</strong>.
            </p>
            
            <blockquote className="border-l-4 border-[#8B7355] pl-4 py-2 my-6 italic text-[#5C4B3F] bg-white">
              "Parce que vous êtes unique, offrez-vous des soins d'exception signés Mélanie Gauthier!"
            </blockquote>
            
            <p>
              Contribuer à votre bien-être est un plaisir. Vous compter parmi ma clientèle est un privilège. Merci de votre fidélité. Grâce à vous, la petite fille en moi réalise son rêve jour après jour. C'est là toute la beauté de mon travail.
            </p>
            <p className="font-medium text-[#5C4B3F]">
              Au plaisir!
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-[#5C4B3F]/10">
            <p className="font-['Playfair_Display'] text-lg text-[#5C4B3F]">Mélanie Gauthier</p>
            <p className="text-[#8B7355] text-sm">Esthéticienne-propriétaire</p>
          </div>

          <a 
            href="#contact" 
            className="inline-flex items-center gap-2 px-6 py-3 mt-6 bg-[#5C4B3F] text-white text-sm uppercase tracking-wider hover:bg-[#8B7355] transition-colors"
          >
            Me contacter
          </a>
        </div>
      </div>
    </div>
  </section>
);

// 5. FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Est-ce que l'épilation laser fait mal?",
      answer: "Avec la technologie Soprano ICE Platinum, le traitement est pratiquement indolore. Le système de refroidissement intégré assure votre confort tout au long de la séance."
    },
    {
      question: "Combien de séances sont nécessaires?",
      answer: "En général, environ 8 à 12 séances sont recommandées pour des résultats optimaux. Cela varie selon la zone traitée et votre type de peau."
    },
    {
      question: "Y a-t-il un entretien après les traitements?",
      answer: "Après la série de traitements initiaux, une séance d'entretien annuelle peut être recommandée pour maintenir les résultats. Certaines personnes n'en ont pas besoin du tout."
    },
    {
      question: "Comment se préparer à la première visite?",
      answer: "Évitez l'exposition au soleil 2 semaines avant, ne pas épiler à la cire ou à la pince, et rasez la zone 24h avant. Une consultation gratuite est offerte pour évaluer vos besoins."
    },
    {
      question: "Le laser convient-il à tous les types de peau?",
      answer: "Oui! Le Soprano ICE Platinum est efficace sur tous les types de peau, y compris les peaux bronzées et foncées, grâce à sa technologie 3-en-1."
    }
  ];

  return (
    <section id="faq" className="py-20 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-2">FAQ</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#5C4B3F]">
            Questions fréquentes
          </h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className="border border-[#5C4B3F]/10"
              data-testid={`faq-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F9F8F6] transition-colors"
              >
                <span className="text-[#5C4B3F] font-medium pr-4">{faq.question}</span>
                <ChevronDown 
                  size={20} 
                  className={`text-[#8B7355] transition-transform flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`} 
                />
              </button>
              {openIndex === index && (
                <div className="px-5 pb-5">
                  <p className="text-[#5C4B3F]/70">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 6. FINAL CTA - Contact Section
const ContactSection = () => {
  const [showPhone, setShowPhone] = useState(false);
  const [showSMS, setShowSMS] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedSMS, setCopiedSMS] = useState(false);

  const handlePhoneReveal = () => {
    setShowPhone(true);
    trackLead(); // Track Lead when phone is revealed
  };

  const handleSMSReveal = () => {
    setShowSMS(true);
    trackLead(); // Track Lead when phone is revealed
  };

  const handlePhoneCall = () => {
    trackContact('phone');
  };

  const handleSMSClick = () => {
    trackContact('sms');
  };

  const handleFacebookClick = () => {
    trackContact('facebook');
  };

  const handleInstagramClick = () => {
    trackContact('instagram');
  };

  const copyToClipboard = async (type) => {
    try {
      await navigator.clipboard.writeText(PHONE_NUMBER);
      if (type === 'phone') {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      } else {
        setCopiedSMS(true);
        setTimeout(() => setCopiedSMS(false), 2000);
      }
      toast.success("Numéro copié!");
    } catch (err) {
      toast.error("Erreur lors de la copie");
    }
  };

  return (
    <section id="contact" className="py-20 bg-[#5C4B3F]" data-testid="contact-section">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <p className="text-white/60 text-sm tracking-widest uppercase mb-2">Prête?</p>
        <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-4">
          Réservez votre rendez-vous
        </h2>
        <p className="text-white/70 mb-10 max-w-xl mx-auto">
          Toutes les réservations se font en ligne, 24h/24, de façon simple et rapide.
        </p>

        {/* Bouton principal : Réservation en ligne Square */}
        <div className="mb-10 flex justify-center">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackSchedule()}
            data-testid="contact-booking-cta"
            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-[#5C4B3F] text-sm uppercase tracking-wider font-medium hover:bg-[#F9F8F6] transition-all duration-300 hover:scale-105 shadow-lg"
          >
            <Calendar size={20} />
            Réserver en ligne
          </a>
        </div>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/20"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-[#5C4B3F] px-4 text-white/60 text-xs uppercase tracking-widest">Une question ?</span>
          </div>
        </div>

        <p className="text-white/60 text-sm mb-8 max-w-lg mx-auto">
          Pour toute information ou demande, contactez-moi directement. Les réservations se font exclusivement via le bouton ci-dessus.
        </p>

        {/* Contact Options */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {/* Phone */}
          {!showPhone ? (
            <button 
              onClick={handlePhoneReveal}
              data-testid="contact-phone-reveal"
              className="p-6 bg-white/10 hover:bg-white/20 transition-colors text-center"
            >
              <Phone className="mx-auto mb-3 text-white" size={28} />
              <p className="text-white font-medium">Appeler</p>
              <p className="text-white/60 text-sm">Voir le numéro</p>
            </button>
          ) : (
            <div className="p-6 bg-white text-center">
              <Phone className="mx-auto mb-3 text-[#5C4B3F]" size={28} />
              <p className="text-[#5C4B3F] font-medium mb-2">{PHONE_NUMBER.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}</p>
              <div className="flex gap-2">
                <a href={`tel:${PHONE_NUMBER}`} onClick={handlePhoneCall} className="flex-1 py-2 bg-[#5C4B3F] text-white text-xs">Appeler</a>
                <button onClick={() => copyToClipboard('phone')} className="flex-1 py-2 bg-[#8B7355] text-white text-xs">
                  {copiedPhone ? 'Copié!' : 'Copier'}
                </button>
              </div>
            </div>
          )}

          {/* SMS */}
          {!showSMS ? (
            <button 
              onClick={handleSMSReveal}
              data-testid="contact-sms-reveal"
              className="p-6 bg-white/10 hover:bg-white/20 transition-colors text-center"
            >
              <MessageSquare className="mx-auto mb-3 text-white" size={28} />
              <p className="text-white font-medium">SMS</p>
              <p className="text-white/60 text-sm">Voir le numéro</p>
            </button>
          ) : (
            <div className="p-6 bg-white text-center">
              <MessageSquare className="mx-auto mb-3 text-[#5C4B3F]" size={28} />
              <p className="text-[#5C4B3F] font-medium mb-2">{PHONE_NUMBER.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}</p>
              <div className="flex gap-2">
                <a href={`sms:${PHONE_NUMBER}`} onClick={handleSMSClick} className="flex-1 py-2 bg-[#5C4B3F] text-white text-xs">SMS</a>
                <button onClick={() => copyToClipboard('sms')} className="flex-1 py-2 bg-[#8B7355] text-white text-xs">
                  {copiedSMS ? 'Copié!' : 'Copier'}
                </button>
              </div>
            </div>
          )}

          {/* Facebook (remplace Messenger) */}
          <a 
            href={SOCIAL_LINKS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleFacebookClick}
            data-testid="contact-facebook-link"
            className="p-6 bg-white/10 hover:bg-white/20 transition-colors text-center"
          >
            <Facebook className="mx-auto mb-3 text-white" size={28} />
            <p className="text-white font-medium">Facebook</p>
            <p className="text-white/60 text-sm">Voir la page</p>
          </a>

          {/* Instagram */}
          <a 
            href={SOCIAL_LINKS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleInstagramClick}
            data-testid="contact-instagram-link"
            className="p-6 bg-white/10 hover:bg-white/20 transition-colors text-center"
          >
            <Instagram className="mx-auto mb-3 text-white" size={28} />
            <p className="text-white font-medium">Instagram</p>
            <p className="text-white/60 text-sm">Message privé</p>
          </a>
        </div>

        {/* Address */}
        <div className="pt-8 border-t border-white/20">
          <p className="text-white/60 text-sm mb-2">Nous trouver</p>
          <a 
            href="https://maps.google.com/maps/place//data=!4m2!3m1!1s0x4cc9ab8aa7256d13:0xe403ec562dc79b5b"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white hover:text-white/80 transition-colors"
          >
            Ouvrir dans Google Maps →
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="py-8 bg-[#3D3229] text-white/60 text-center text-sm">
    <div className="max-w-6xl mx-auto px-4">
      <p className="mb-2">© {new Date().getFullYear()} Esthétique Signé Mélanie Gauthier. Tous droits réservés.</p>
      <p>
        Site conçu par{" "}
        <a 
          href="https://lesamouraiduweb.com" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-white/80 hover:text-white transition-colors"
        >
          Le Samouraï du Web
        </a>
      </p>
    </div>
  </footer>
);

// Homepage Component
const HomePage = () => (
  <>
    <Navigation />
    <StickyBookingButton />
    <HeroSection />
    <ServicesSection />
    <LaserPricingSection />
    <SocialProofSection />
    <AboutSection />
    <FAQSection />
    <ContactSection />
    <Footer />
  </>
);

// Main App with Router
function App() {
  return (
    <Router>
      <div className="App" data-testid="app-container">
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/epilation-laser" element={<EpilationLaserPage />} />
          <Route path="/soins-visage" element={<SoinsVisagePage />} />
          <Route path="/extensions-cils" element={<ExtensionsCilsPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
